import { api, getApiUrl, PaginatedList } from 'utils/api';
import { MIN_SEARCH_QUERY_LENGTH } from 'utils/constants';
import { SchoolResponse } from './schools';
import {
  AdminSubmissionFilters,
  CreateResqlExpertSubmissionRequest,
  ResqlExpertSubmissionListItem,
  ResqlExpertSubmissionResponse,
  UpdateAdminSubmissionRequest,
} from './resqlexpert.d';

const BASE_URL = getApiUrl('/resql-expert');
const ADMIN_URL = `${BASE_URL}/admin`;

interface ListIntervenerParams {
  query?: string;
  page: number;
  size: number;
  signal?: AbortSignal;
}

interface ListAdminParams extends ListIntervenerParams {
  filters: AdminSubmissionFilters;
}

const appendQuery = (params: URLSearchParams, query: string | undefined) => {
  if (query && query.length >= MIN_SEARCH_QUERY_LENGTH) params.set('query', query);
};

const buildIntervenerListUrl = ({ query, page, size }: ListIntervenerParams): string => {
  const params = new URLSearchParams();
  appendQuery(params, query);
  params.set('page', page.toString());
  params.set('size', size.toString());
  return `${BASE_URL}/submissions?${params.toString()}`;
};

const buildAdminListUrl = ({ query, filters, page, size }: ListAdminParams): string => {
  const params = new URLSearchParams();
  appendQuery(params, query);
  if (filters.school_id) params.set('school_id', filters.school_id.toString());
  if (filters.submitted_by) params.set('submitted_by', filters.submitted_by.toString());
  if (filters.assigned_to) params.set('assigned_to', filters.assigned_to.toString());
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  params.set('page', page.toString());
  params.set('size', size.toString());
  return `${ADMIN_URL}/submissions?${params.toString()}`;
};

export const resqlExpertApi = {
  getSchools: (): Promise<SchoolResponse[]> =>
    api(`${BASE_URL}/schools`, { method: 'GET' }),

  getSubmissionDetail: (submissionId: number): Promise<ResqlExpertSubmissionResponse> =>
    api(`${BASE_URL}/submissions/${submissionId}`, { method: 'GET' }),

  createSubmission: (request: CreateResqlExpertSubmissionRequest): Promise<ResqlExpertSubmissionResponse> =>
    api(`${BASE_URL}/submissions`, {
      method: 'POST',
      payload: request,
    }),

  listIntervener: (params: ListIntervenerParams): Promise<PaginatedList<ResqlExpertSubmissionListItem[]>> =>
    api(buildIntervenerListUrl(params), { method: 'GET', signal: params.signal }),
};

export const resqlExpertAdminApi = {
  getSubmissionDetail: (submissionId: number): Promise<ResqlExpertSubmissionResponse> =>
    api(`${ADMIN_URL}/submissions/${submissionId}`, { method: 'GET' }),

  updateSubmission: (submissionId: number, request: UpdateAdminSubmissionRequest): Promise<ResqlExpertSubmissionResponse> =>
    api(`${ADMIN_URL}/submissions/${submissionId}`, {
      method: 'PUT',
      payload: request,
    }),

  listAdmin: (params: ListAdminParams): Promise<PaginatedList<ResqlExpertSubmissionListItem[]>> =>
    api(buildAdminListUrl(params), { method: 'GET', signal: params.signal }),
};
